import type { ForwardRefRenderFunction, SyntheticEvent } from "react";
import { forwardRef, useImperativeHandle, useRef } from "react";

import classNames from "classnames";

import { DotsLoader, ListItemSeparator, ListItemWebsite, type IAutocompleteProps } from "@similarweb/ui-components";
import { Stack } from "@similarweb/ui-components/foundations";

import { autocompleteStates } from "app/components/compare/WebsiteQueryBarTypes";
import { getWebsiteResults } from "app/services/solutions2Services/HomepageDataFetchers/NewModulesHomepageDataFetcher";

import { useTranslation } from "../WithTranslation/src/I18n";

import { ScrollAreaWrap } from "./components/ScrollAreaWrapper";
import { AutocompleteStyled, StyledItemWrapper, StyledSecondColumn } from "./styles";
import type {
    IAutocompleteWebsitesCompareItemProps,
    IAutocompleteWebsitesCompareItemWrap,
    ICompareItem,
    IWebsitePageResult,
} from "./types";
import { filterOutIllegalWebsiteQueryBarItems, getListItemsGroupsContent, isValidSiteItem } from "./utils";

const toAutocompleteWebsiteShape = <T extends { name: string; image: string }>(website: T) => {
    return { Domain: website.name, Favicon: website.image };
};

const normalizedSimilarSite = (item: FixmeType) => {
    return {
        name: item.Domain,
        icon: item.Favicon,
    };
};

const normalizedSearches = (item: FixmeType) => {
    return {
        name: item.name,
        icon: item.image,
    };
};

const AutocompleteWebsitesCompareItemRenderFunction: ForwardRefRenderFunction<
    IAutocompleteWebsitesCompareItemWrap,
    IAutocompleteWebsitesCompareItemProps
> = (props, ref) => {
    const translate = useTranslation();
    const {
        onClick,
        similarSites,
        excludes,
        autocompleteProps,
        onCloseButtonClick,
        className,
        onKeyUp,
        onArrowKeyPress,
        overrideEnterFunc,
        overrideGetWebsites = getWebsiteResults,
        AutocompleteWidth,
        customItemStructure,
        secondColumnName,
        secondColumnFormatter,
        modifySearchResult,
        renderSimilarSitesHead,
        renderSearchResultsHead,
        renderNoDataSimilarsitesComponent,
        renderNoDataSearchesComponent,
        renderNoDataErrorComponent,
        autoCompleteDropdownRef,
    } = props;
    const autocompleteRef = useRef<FixmeType>(null);

    useImperativeHandle(ref, () => ({
        truncateResults: (force: boolean) => {
            autocompleteRef.current?.truncateResults(force);
        },
    }));

    const onItemClick = (item: IWebsitePageResult, type: "query" | "similar") => () => {
        onClick(item, type);
    };

    const onEnter = (event: SyntheticEvent, item?: ICompareItem) => overrideEnterFunc?.(event, item);

    const renderItemCreator = (type: string) => (item: FixmeType) => {
        return { ...item, type, props: { onClick: onItemClick(item, type as "query" | "similar") } };
    };

    const getListItems = async (query?: string) => {
        if (query) {
            try {
                const results = await overrideGetWebsites(query);
                if (!results.length) {
                    return [autocompleteStates.NO_DATA_SEARCHES];
                }
                const modifiedSearchResult = modifySearchResult ? modifySearchResult(results) : results;

                const filteredResults = modifiedSearchResult
                    .filter(isValidSiteItem(excludes ?? []))
                    .map((item: FixmeType) => {
                        const renderer = customItemStructure || normalizedSearches;
                        return renderItemCreator("query")(renderer(item));
                    });
                return filterOutIllegalWebsiteQueryBarItems(filteredResults);
            } catch (_e) {
                return [autocompleteStates.ERROR];
            }
        }

        if (
            similarSites[0] === autocompleteStates.NO_DATA_SIMILARSITES ||
            similarSites[0] === autocompleteStates.ERROR
        ) {
            return similarSites;
        }

        const result = similarSites
            .filter((x: FixmeType) => !(excludes ?? []).find((exclude) => exclude.name === (x.Domain || x.name)))
            .map((item: FixmeType) => {
                const renderer = customItemStructure || normalizedSimilarSite;
                return renderItemCreator("similar")(renderer(item));
            });

        return result;
    };

    const getSimilarSitesHead = () => {
        if (typeof renderSimilarSitesHead === "function") {
            return renderSimilarSitesHead();
        }

        return (
            <ListItemSeparator>
                {translate("autocomplete.websitesCompare.seperator.text.similarSites")}
            </ListItemSeparator>
        );
    };

    const getSearchResultsHead = () => {
        if (typeof renderSearchResultsHead === "function") {
            return renderSearchResultsHead();
        }

        return null;
    };

    const renderItems = ({
        listItems,
        selectedItemId,
    }: // @ts-ignore FIXME: strict migration - IAutocompleteProps["renderItems"] may be undefined — extracting Parameters from optional function
    Parameters<IAutocompleteProps["renderItems"]>[0] & {
        listItems: Array<ICompareItem | autocompleteStates>;
    }) => {
        if (!listItems.length) {
            return null;
        }

        const itemCreator = (item: ICompareItem, index: number) => {
            const active = index === selectedItemId;
            // @ts-ignore FIXME: strict migration - secondColumnName dynamically indexes ICompareItem — index type not statically known
            const secondColumnCellValue = secondColumnName ? item[secondColumnName] : null;
            const secondColumnContent =
                secondColumnFormatter && secondColumnName
                    ? secondColumnFormatter(secondColumnCellValue)
                    : secondColumnCellValue;

            return (
                <StyledItemWrapper
                    key={`${item.name}_${index}`}
                    className={classNames({ active, disabled: item.disabled })}
                >
                    <ListItemWebsite
                        className={"SimilarWebsiteItem"}
                        isActive={active}
                        key={`${item.name}_${index}`}
                        img={item.icon}
                        text={item.name}
                        onClick={item.props.onClick}
                        customRenderData={<StyledSecondColumn>{secondColumnContent}</StyledSecondColumn>}
                    />
                </StyledItemWrapper>
            );
        };

        const { similar, searchResults } = getListItemsGroupsContent({
            listItems,
            renderListItem: itemCreator,
            renderNoSearchData: renderNoDataSearchesComponent,
            renderNoSimilarsitesData: renderNoDataSimilarsitesComponent,
            renderNoDataError: renderNoDataErrorComponent,
        });

        return (
            <div
                className="ListItemsContainer"
                ref={autoCompleteDropdownRef}
            >
                <Stack gap="8px">
                    {similar.length > 0 ? (
                        <section>
                            {getSimilarSitesHead()}
                            <ScrollAreaWrap>{similar}</ScrollAreaWrap>
                        </section>
                    ) : null}
                    {searchResults.length > 0 ? (
                        <section>
                            {getSearchResultsHead()}
                            <ScrollAreaWrap>{searchResults}</ScrollAreaWrap>
                        </section>
                    ) : null}
                </Stack>
            </div>
        );
    };

    return (
        <AutocompleteStyled
            className={className}
            isFocused
            autoFocus
            loadingComponent={<DotsLoader />}
            preventTruncateUnlessForced
            resetValueOnSelect
            floating
            debounce={250}
            maxResults={20}
            onKeyUp={onKeyUp}
            onArrowKeyPress={onArrowKeyPress}
            overrideEnterFunc={onEnter}
            initialSelectedItemId={-1}
            ref={autocompleteRef}
            onCloseClick={onCloseButtonClick}
            getListItems={getListItems}
            renderItems={renderItems}
            width={AutocompleteWidth}
            {...autocompleteProps}
        />
    );
};
export const AutocompleteWebsitesCompareItem = forwardRef(AutocompleteWebsitesCompareItemRenderFunction);
